import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    Modal,
    Form,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Label,
    Input,
    Button,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    createBazaar,
    editBazaar,
    deleteBazaar,
} from "../../api/bazaars";
import TableContainer from "../../Components/Common/TableContainer";
import DeleteModal from "../../Components/Common/DeleteModal";
import Loader from "../../Components/Common/Loader";
import "react-toastify/dist/ReactToastify.css";
import { getBazaarsQuery, getRegionsQuery } from "../../queries";
import { useTranslation } from "react-i18next";

const Bazaars = () => {
    const [page, setPage] = useState(1);
    const [regionFilter, setRegionFilter] = useState("");
    const [districtFilter, setDistrictFilter] = useState("");
    const [modal, setModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [bazaar, setBazaar] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);

    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: "AIzaSyAbvyBxmMbFhrzP9Z8moyYr6dCr-pzjhBE"
    });

    const { data: bazaars } = useQuery({
        ...getBazaarsQuery({
            size: "10",
            page,
            regionId: districtFilter || regionFilter || undefined,
        }),
    });

    const { data: regions } = useQuery({
        ...getRegionsQuery({
            size: "999", // Fetch enough regions for dropdown
            page: 1,
        }),
    });

    const toggle = useCallback(() => {
        if (modal) {
            setModal(false);
            setBazaar(null);
        } else {
            setModal(true);
        }
    }, [modal]);


    const onDelete = () => {
        deleteBazaar(deleteModal)
            .then(() => {
                queryClient.invalidateQueries({ queryKey: ["bazaars"] });
                setDeleteModal(false);
            })
            .catch((err) => {
                toast.error(err?.response?.data?.error?.message || t("Error deleting bazaar"));
                setDeleteModal(false);
            });
    };

    const schema = Yup.object({
        stir: Yup.string().required(t("STIR is required")),
        parentRegion: Yup.string().required(t("Region is required")),
        districtSelect: Yup.string().required(t("District is required")),
        regionId: Yup.number().required(t("Region is required")), // This will effectively be validated by parentRegion logic + updates
        address: Yup.string().required(t("Address is required")),
        latitude: Yup.string().required(t("Latitude is required")),
        longitude: Yup.string().required(t("Longitude is required")),
        locales: Yup.array()
            .of(
                Yup.object({
                    localeKey: Yup.string()
                        .oneOf(["uz", "ru", "uzc"], t("Invalid locale key"))
                        .required(t("Locale key is required")),
                    title: Yup.string().required(t("Title is required")),
                })
            )
            .length(3, t("Must have exactly 3 locales")),
    });

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            stir: (bazaar && bazaar.stir) || "",
            parentRegion: (() => {
                if (!bazaar || !regions?.data?.data) return "";
                const currentRegion = regions.data.data.find(r => r.id === bazaar.regionId);
                if (currentRegion?.regionType === "district" || currentRegion?.regionType === "Town") {
                    return currentRegion.upperId || "";
                }
                return bazaar.regionId || "";
            })(),
            districtSelect: (() => {
                if (!bazaar || !regions?.data?.data) return "";
                const currentRegion = regions.data.data.find(r => r.id === bazaar.regionId);
                if (currentRegion?.regionType === "district" || currentRegion?.regionType === "Town") {
                    return bazaar.regionId || "";
                }
                return "";
            })(),
            regionId: (bazaar && bazaar.regionId) || "",
            address: (bazaar && bazaar.address) || "",
            latitude: (bazaar && bazaar.latitude) || "",
            longitude: (bazaar && bazaar.longitude) || "",
            locales: useMemo(() => {
                const locales = bazaar?.locales || [];
                const localeKeys = ["uz", "ru", "uzc"];
                return localeKeys.map((localeKey) => {
                    const existingLocale = locales.find(
                        (locale) => locale.localeKey === localeKey
                    );
                    return existingLocale || { localeKey, title: "" };
                });
            }, [bazaar]),
        },
        validationSchema: schema,
        onSubmit: (values) => {
            const payload = { ...values };
            delete payload.parentRegion;
            delete payload.districtSelect;

            if (isEdit) {
                editBazaar(bazaar.id, payload)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ["bazaars"] });
                        toggle();
                    })
                    .catch((err) => {
                        console.error(err);
                        toast.error(t("Error updating bazaar"));
                    });
            } else {
                createBazaar(payload)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ["bazaars"] });
                        toggle();
                    })
                    .catch((err) => {
                        console.error(err);
                        toast.error(t("Error creating bazaar"));
                    });
            }
        },
    });


    // Effect to update local 'bazaar' state when the main list updates (for image changes to reflect in modal)
    useEffect(() => {
        if (bazaar && bazaars?.data?.data) {
            const updatedBazaar = bazaars.data.data.find(b => b.id === bazaar.id);
            if (updatedBazaar) {
                setBazaar(updatedBazaar);
            }
        }
    }, [bazaars, bazaar?.id]);


    const columns = useMemo(
        () => [
            {
                header: t("Name"),
                cell: ({ row }) => {
                    const locales = row.original.locales || [];
                    const ruTitle = locales.find((l) => l.localeKey === "ru")?.title;
                    return ruTitle || locales[0]?.title || t("Unknown");
                },
            },
            {
                header: t("STIR"),
                accessorKey: "stir",
            },
            {
                header: t("Region"),
                cell: ({ row }) => {
                    const region = regions?.data?.data?.find(r => r.id === row.original.regionId);
                    const name = region?.locales?.find(l => l.localeKey === 'ru')?.title || region?.locales?.[0]?.title;
                    return name || row.original.regionId;
                }
            },
            {
                header: t("Address"),
                accessorKey: "address",
            },
            {
                header: t("Actions"),
                cell: ({ row }) => (
                    <>
                        <button
                            className="btn btn-sm btn-primary"
                            style={{ marginLeft: "10px" }}
                            onClick={() => {
                                setBazaar(row.original);
                                setIsEdit(true);
                                toggle();
                            }}
                        >
                            <i className="ri-edit-fill"></i>
                        </button>
                        <button
                            className="btn btn-sm btn-danger"
                            style={{ marginLeft: "10px" }}
                            onClick={() => setDeleteModal(row.original.id)}
                        >
                            <i className="ri-delete-bin-6-fill"></i>
                        </button>
                    </>
                ),
            },
        ],
        [toggle, t, regions]
    );

    document.title = "Bazaars | Admin";

    return (
        <div className="page-content">
            <DeleteModal
                show={!!deleteModal}
                onDeleteClick={onDelete}
                onCloseClick={() => setDeleteModal(false)}
            />


            <Container fluid>
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="border-0 ">
                                <Row className="align-items-center gy-3">
                                    <div className="col-sm">
                                        <h5 className="mb-0 fw-bold">{t("Bazaars")}</h5>
                                    </div>
                                    <div className="col-sm-auto">
                                        <div className="d-flex gap-1 flex-wrap">
                                            <select
                                                className="form-select"
                                                value={regionFilter}
                                                onChange={(e) => {
                                                    setRegionFilter(e.target.value);
                                                    setDistrictFilter(""); // Reset district when region changes
                                                }}
                                                style={{ width: "200px" }}
                                            >
                                                <option value="">{t("All Regions")}</option>
                                                {regions?.data?.data
                                                    ?.filter((r) => r.regionType === "region")
                                                    ?.map((r) => {
                                                        const name = r.locales?.find((l) => l.localeKey === "ru")?.title || r.locales?.[0]?.title;
                                                        return (
                                                            <option key={r.id} value={r.id}>
                                                                {name}
                                                            </option>
                                                        );
                                                    })}
                                            </select>

                                            <select
                                                className="form-select"
                                                value={districtFilter}
                                                onChange={(e) => setDistrictFilter(e.target.value)}
                                                style={{ width: "200px" }}
                                                disabled={!regionFilter}
                                            >
                                                <option value="">{t("All Districts")}</option>
                                                {regions?.data?.data
                                                    ?.filter((r) =>
                                                        (r.regionType === "district") &&
                                                        (!regionFilter || r.upperId === parseInt(regionFilter))
                                                    )
                                                    ?.map((r) => {
                                                        const name = r.locales?.find((l) => l.localeKey === "ru")?.title || r.locales?.[0]?.title;
                                                        return (
                                                            <option key={r.id} value={r.id}>
                                                                {name}
                                                            </option>
                                                        );
                                                    })}
                                            </select>

                                            <button
                                                type="button"
                                                className="btn btn-success add-btn"
                                                onClick={() => {
                                                    setIsEdit(false);
                                                    setBazaar(null); // Reset for new entry
                                                    toggle();
                                                }}
                                            >
                                                <i className="ri-add-line align-bottom me-1"></i>{" "}
                                                {t("create")}
                                            </button>
                                        </div>
                                    </div>
                                </Row>
                            </CardHeader>
                            <div className="py-2"></div>
                            <div className="card-body pt-0">
                                {bazaars?.data?.data ? (
                                    <TableContainer
                                        columns={columns}
                                        data={bazaars.data.data}
                                        isAddUserList={false}
                                        customPageSize={10}
                                        pagination={{
                                            setPage: setPage,
                                            page: page,
                                            total: bazaars?.pagination?.TotalPages || 1,
                                        }}
                                        divClass="table-responsive table-card mb-3"
                                        tableClass="align-middle table-nowrap mb-0"
                                        theadClass="table-light table-nowrap"
                                        thClass="text-muted"
                                    />
                                ) : (
                                    <Loader />
                                )}

                                {/* Create/Edit Modal */}
                                <Modal isOpen={modal} toggle={toggle} centered>
                                    <ModalHeader className="bg-light p-3" toggle={toggle}>
                                        {!!isEdit ? t("Edit Bazaar") : t("Create Bazaar")}
                                    </ModalHeader>
                                    <Form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            validation.handleSubmit();
                                            return false;
                                        }}
                                    >
                                        <ModalBody>
                                            <div className="mb-3">
                                                <Label htmlFor="stir" className="form-label">{t("STIR")}</Label>
                                                <Input
                                                    name="stir"
                                                    placeholder={t("STIR")}
                                                    onChange={validation.handleChange}
                                                    onBlur={validation.handleBlur}
                                                    value={validation.values.stir}
                                                    invalid={validation.touched.stir && validation.errors.stir ? true : false}
                                                />
                                                {validation.touched.stir && validation.errors.stir ? (
                                                    <div className="text-danger">{validation.errors.stir}</div>
                                                ) : null}
                                            </div>

                                            <div className="mb-3">
                                                <Label htmlFor="parentRegion" className="form-label">{t("Region")}</Label>
                                                <Input
                                                    type="select"
                                                    name="parentRegion"
                                                    onChange={(e) => {
                                                        validation.handleChange(e);
                                                        // When parent region changes, reset district
                                                        validation.setFieldValue("districtSelect", "");
                                                        validation.setFieldValue("regionId", "");
                                                    }}
                                                    onBlur={validation.handleBlur}
                                                    value={validation.values.parentRegion}
                                                    invalid={validation.touched.parentRegion && validation.errors.parentRegion ? true : false}
                                                >
                                                    <option value="">{t("Select Region")}</option>
                                                    {regions?.data?.data
                                                        ?.filter((r) => r.regionType === "region" || r.regionType === "City")
                                                        ?.map((r) => {
                                                            const name = r.locales?.find(l => l.localeKey === 'ru')?.title || r.locales?.[0]?.title;
                                                            return <option key={r.id} value={r.id}>{name}</option>
                                                        })}
                                                </Input>
                                                {validation.touched.parentRegion && validation.errors.parentRegion ? (
                                                    <div className="text-danger">{validation.errors.parentRegion}</div>
                                                ) : null}
                                            </div>

                                            <div className="mb-3">
                                                <Label htmlFor="districtSelect" className="form-label">{t("District")}</Label>
                                                <Input
                                                    type="select"
                                                    name="districtSelect"
                                                    onChange={(e) => {
                                                        validation.handleChange(e);
                                                        validation.setFieldValue("regionId", e.target.value);
                                                    }}
                                                    onBlur={validation.handleBlur}
                                                    value={validation.values.districtSelect}
                                                    disabled={!validation.values.parentRegion}
                                                    invalid={validation.touched.districtSelect && validation.errors.districtSelect ? true : false}
                                                >
                                                    <option value="">{t("Select District")}</option>
                                                    {regions?.data?.data
                                                        ?.filter((r) =>
                                                            (r.regionType === "district" || r.regionType === "Town") &&
                                                            (r.upperId == validation.values.parentRegion)
                                                        )
                                                        ?.map((r) => {
                                                            const name = r.locales?.find(l => l.localeKey === 'ru')?.title || r.locales?.[0]?.title;
                                                            return <option key={r.id} value={r.id}>{name}</option>
                                                        })}
                                                </Input>
                                                {validation.touched.districtSelect && validation.errors.districtSelect ? (
                                                    <div className="text-danger">{validation.errors.districtSelect}</div>
                                                ) : null}
                                            </div>

                                            <div className="mb-3">
                                                <Label htmlFor="address" className="form-label">{t("Address")}</Label>
                                                <Input
                                                    name="address"
                                                    placeholder={t("Address")}
                                                    onChange={validation.handleChange}
                                                    onBlur={validation.handleBlur}
                                                    value={validation.values.address}
                                                    invalid={validation.touched.address && validation.errors.address ? true : false}
                                                />
                                                {validation.touched.address && validation.errors.address ? (
                                                    <div className="text-danger">{validation.errors.address}</div>
                                                ) : null}
                                            </div>

                                            <div className="mb-3">
                                                <Label>{t("Location")}</Label>
                                                <div style={{ height: "300px", width: "100%", marginBottom: "10px" }}>
                                                    {isLoaded ? (
                                                        <GoogleMap
                                                            mapContainerStyle={{ height: "100%", width: "100%" }}
                                                            center={
                                                                validation.values.latitude && validation.values.longitude
                                                                    ? {
                                                                        lat: parseFloat(validation.values.latitude),
                                                                        lng: parseFloat(validation.values.longitude),
                                                                    }
                                                                    : { lat: 41.2995, lng: 69.2401 } // Default to Tashkent
                                                            }
                                                            zoom={12}
                                                            onClick={(e) => {
                                                                const lat = e.latLng.lat();
                                                                const lng = e.latLng.lng();
                                                                validation.setFieldValue("latitude", String(lat));
                                                                validation.setFieldValue("longitude", String(lng));
                                                            }}
                                                        >
                                                            {validation.values.latitude && validation.values.longitude && (
                                                                <Marker
                                                                    position={{
                                                                        lat: parseFloat(validation.values.latitude),
                                                                        lng: parseFloat(validation.values.longitude),
                                                                    }}
                                                                />
                                                            )}
                                                        </GoogleMap>
                                                    ) : (
                                                        <div>Loading map...</div>
                                                    )}
                                                </div>
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <Label htmlFor="latitude">{t("Latitude")}</Label>
                                                        <Input
                                                            name="latitude"
                                                            placeholder={t("Latitude")}
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.latitude}
                                                            readOnly
                                                            invalid={validation.touched.latitude && validation.errors.latitude ? true : false}
                                                        />
                                                        {validation.touched.latitude && validation.errors.latitude ? (
                                                            <div className="text-danger">{validation.errors.latitude}</div>
                                                        ) : null}
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <Label htmlFor="longitude">{t("Longitude")}</Label>
                                                        <Input
                                                            name="longitude"
                                                            placeholder={t("Longitude")}
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.longitude}
                                                            readOnly
                                                            invalid={validation.touched.longitude && validation.errors.longitude ? true : false}
                                                        />
                                                        {validation.touched.longitude && validation.errors.longitude ? (
                                                            <div className="text-danger">{validation.errors.longitude}</div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>

                                            {["uz", "ru", "uzc"].map((key) => {
                                                const localeIndex = validation.values.locales.findIndex(
                                                    (locale) => locale.localeKey === key
                                                );
                                                return localeIndex !== -1 ? (
                                                    <div className="mb-3" key={key}>
                                                        <Label className="form-label">{t("Name")} ({key.toUpperCase()})</Label>
                                                        <Input
                                                            name={`locales[${localeIndex}].title`}
                                                            placeholder={`${t("Name")} (${key})`}
                                                            type="text"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.locales[localeIndex].title}
                                                            invalid={
                                                                validation.touched.locales?.[localeIndex]?.title &&
                                                                    validation.errors.locales?.[localeIndex]?.title
                                                                    ? true
                                                                    : false
                                                            }
                                                        />
                                                        {validation.touched.locales?.[localeIndex]?.title &&
                                                            validation.errors.locales?.[localeIndex]?.title ? (
                                                            <div className="text-danger">
                                                                {validation.errors.locales[localeIndex].title}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ) : null;
                                            })}
                                        </ModalBody>
                                        <ModalFooter>
                                            <Button color="light" onClick={toggle}>{t("Close")}</Button>
                                            <Button color="success" type="submit">{isEdit ? t("edit") : t("create")}</Button>
                                        </ModalFooter>
                                    </Form>
                                </Modal>
                                <ToastContainer closeButton={false} limit={1} />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Bazaars;
